import { getEnvironmentConfig } from './envValidation';

// Get validated environment configuration
const config = getEnvironmentConfig();

/**
 * Utility functions for handling property images
 */

// Get configuration from environment variables
const S3_BUCKET = config.s3Config.bucket;
const SUPABASE_PROJECT_URL = config.supabaseUrl;

// Default placeholder image for properties without images
const DEFAULT_PROPERTY_IMAGE = '/placeholder-property.svg';

// Sample placeholder images for variety (used as fallback for testing)
const SAMPLE_IMAGES = [
  '/images/sample-properties/sample_house.png',
  '/images/sample-properties/sample_living_room.png',
  '/images/sample-properties/bedroom_sample.png'
];

/**
 * Get the default property placeholder image
 * @returns The default property placeholder image path
 */
function getDefaultPropertyImage(): string {
  return DEFAULT_PROPERTY_IMAGE;
}

/**
 * Get a random sample image for variety
 * @returns A random sample image path
 */
function getRandomSampleImage(): string {
  const randomIndex = Math.floor(Math.random() * SAMPLE_IMAGES.length);
  return SAMPLE_IMAGES[randomIndex];
}

/**
 * Get the first image from a property's images array with fallback
 * @param images - Array of image URLs or null/undefined
 * @returns The first image URL or the default placeholder image
 */
export function getPropertyImage(images: string[] | null | undefined): string {
  if (images && images.length > 0 && images[0]) {
    const imageUrl = images[0];
    
    // Add null check before calling startsWith
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // If it's an S3 key, construct the full S3 URL
    if (imageUrl && typeof imageUrl === 'string' && !imageUrl.includes('://')) {
      return constructS3Url(imageUrl);
    }
    
    return imageUrl || getDefaultPropertyImage();
  }
  return getDefaultPropertyImage();
}

/**
 * Get multiple images from a property with fallback
 * @param images - Array of image URLs or null/undefined
 * @returns Array of image URLs or empty array
 */
export function getPropertyImages(images: string[] | null | undefined): string[] {
  if (images && images.length > 0) {
    return images
      .filter(img => img && typeof img === 'string' && img.trim() !== '')
      .map(img => {
        // Add null check before calling startsWith
        if (img && typeof img === 'string' && img.startsWith('http')) {
          return img;
        }
        
        // If it's an S3 key, construct the full S3 URL
        if (img && typeof img === 'string' && !img.includes('://')) {
          return constructS3Url(img);
        }
        
        return img;
      });
  }
  return [];
}

/**
 * Check if a property has valid images
 * @param images - Array of image URLs or null/undefined
 * @returns boolean indicating if property has images
 */
export function hasPropertyImages(images: string[] | null | undefined): boolean {
  return !!(images && images.length > 0 && images[0]);
}

/**
 * Handle image load errors by setting a fallback
 * @param event - Image error event
 * @param fallbackSrc - Fallback image source
 */
export function handleImageError(event: Event, fallbackSrc?: string): void {
  const target = event.target as HTMLImageElement;
  const fallback = fallbackSrc || getDefaultPropertyImage();
  if (target.src !== fallback) {
    target.src = fallback;
  }
}

/**
 * Construct S3 URL from image key
 * @param key - S3 image key
 * @returns Full S3 URL
 */
export function constructS3Url(key: string): string {
  if (!key || typeof key !== 'string') return getDefaultPropertyImage();
  
  // Remove any leading slashes
  const cleanKey = key.replace(/^\/+/, '');
  
  // Construct Supabase Storage URL
  return `${SUPABASE_PROJECT_URL}/storage/v1/object/public/${S3_BUCKET}/${cleanKey}`;
}

/**
 * Extract S3 key from Supabase storage URL
 * @param url - Supabase storage URL
 * @returns S3 key or null if invalid
 */
export function extractS3KeyFromUrl(url: string): string | null {
  try {
    // Supabase storage URL format: https://[project].supabase.co/storage/v1/object/public/[bucket]/[key]
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const bucketIndex = pathParts.findIndex(part => part === 'public') + 1;
    if (bucketIndex > 0 && bucketIndex < pathParts.length) {
      return pathParts.slice(bucketIndex + 1).join('/');
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Validate if a URL is a valid Supabase storage URL
 * @param url - URL to validate
 * @returns boolean indicating if URL is valid
 */
export function isValidSupabaseStorageUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.includes('supabase.co') && 
           urlObj.pathname.includes('/storage/v1/object/public/');
  } catch {
    return false;
  }
}

const SUPABASE_OBJECT_PUBLIC = '/storage/v1/object/public/';
const SUPABASE_RENDER_PUBLIC = '/storage/v1/render/image/public/';

/**
 * Resize + WebP via Supabase Storage image API (Pro / enabled projects).
 * Falls back to original URL when not a Supabase public object URL.
 */
export function toSupabaseRenderImageUrl(
  objectPublicUrl: string,
  options: {
    width: number;
    height: number;
    resize?: 'cover' | 'contain' | 'fill';
    format?: 'webp' | 'origin' | 'avif';
    quality?: number;
    /**
     * When true, only `width`, `height`, and `resize` are sent (no format/quality).
     * Matches Supabase Storage image URLs like:
     * `/render/image/public/.../Manila.png?width=640&height=360&resize=cover`
     */
    minimal?: boolean;
  }
): string | null {
  if (!objectPublicUrl || !isValidSupabaseStorageUrl(objectPublicUrl)) return null;
  try {
    const u = new URL(objectPublicUrl);
    const idx = u.pathname.indexOf(SUPABASE_OBJECT_PUBLIC);
    if (idx === -1) return null;
    const tail = u.pathname.slice(idx + SUPABASE_OBJECT_PUBLIC.length);
    const renderPath = `${SUPABASE_RENDER_PUBLIC}${tail}`;
    const params = new URLSearchParams();
    params.set('width', String(options.width));
    params.set('height', String(options.height));
    params.set('resize', options.resize ?? 'cover');
    if (!options.minimal) {
      params.set('format', options.format ?? 'webp');
      params.set('quality', String(options.quality ?? 78));
    }
    return `${u.origin}${renderPath}?${params.toString()}`;
  } catch {
    return null;
  }
}

/**
 * Card grid thumbnail URL for listing grids.
 * Uses the same public object URL as `getPropertyImage` — not the Storage **render** API — so
 * thumbnails still load when Image Transformations are disabled on the Supabase project (render
 * URLs would 404 and the card would fall back to the placeholder).
 */
export function getPropertyCardImageSrc(images: string[] | null | undefined): string {
  return getPropertyImage(images);
}

/**
 * Check if an image URL is an S3 image
 * @param url - Image URL to check
 * @returns boolean indicating if URL is an S3 image
 */
export function isS3Image(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  return isValidSupabaseStorageUrl(url) || (!url.startsWith('http') && !SAMPLE_IMAGES.includes(url) && url !== DEFAULT_PROPERTY_IMAGE);
}

/**
 * Get optimized image URL for different sizes (useful for responsive images)
 * @param imageUrl - Original image URL
 * @param width - Desired width
 * @param height - Desired height
 * @returns Optimized image URL
 */
export function getOptimizedImageUrl(imageUrl: string, width: number, height: number): string {
  if (!imageUrl || typeof imageUrl !== 'string') return getDefaultPropertyImage();

  const baseUrl = imageUrl.startsWith('http') ? imageUrl : constructS3Url(imageUrl);

  if (isValidSupabaseStorageUrl(baseUrl)) {
    const rendered = toSupabaseRenderImageUrl(baseUrl, {
      width,
      height,
      resize: 'cover',
      format: 'webp',
      quality: 80,
    });
    if (rendered) return rendered;
  }

  if (isS3Image(imageUrl)) {
    return baseUrl;
  }

  return imageUrl;
}
